import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setLanguage } from '../i18n';
import './CoachDemoLandingPage.css';

const ROSTER = [
  { id: 'ana', name: 'Ana Ribeiro', raceKey: 'hm', tsb: -8, load: 'optimal', pending: true },
  { id: 'lucas', name: 'Lucas Mendes', raceKey: 'ten', tsb: -22, load: 'load', pending: true },
  { id: 'marina', name: 'Marina Costa', raceKey: 'five', tsb: 14, load: 'recovered', pending: false },
  { id: 'pedro', name: 'Pedro Alves', raceKey: 'marathon', tsb: -34, load: 'risk', pending: true },
];

const WORKOUTS = {
  ana: [
    { dayKey: 'd1', titleKey: 'easy', km: '8 km' },
    { dayKey: 'd2', titleKey: 'intervals', km: '10 km' },
    { dayKey: 'd4', titleKey: 'tempo', km: '9 km' },
    { dayKey: 'd6', titleKey: 'long', km: '18 km' },
  ],
  lucas: [
    { dayKey: 'd1', titleKey: 'easy', km: '6 km' },
    { dayKey: 'd3', titleKey: 'hills', km: '8 km' },
    { dayKey: 'd5', titleKey: 'intervals', km: '7 km' },
    { dayKey: 'd7', titleKey: 'long', km: '14 km' },
  ],
  marina: [
    { dayKey: 'd2', titleKey: 'easy', km: '5 km' },
    { dayKey: 'd4', titleKey: 'strides', km: '6 km' },
    { dayKey: 'd6', titleKey: 'easy', km: '8 km' },
  ],
  pedro: [
    { dayKey: 'd1', titleKey: 'recovery', km: '8 km' },
    { dayKey: 'd3', titleKey: 'easy', km: '10 km' },
    { dayKey: 'd5', titleKey: 'tempo', km: '12 km' },
    { dayKey: 'd7', titleKey: 'long', km: '22 km' },
  ],
};

const WEEK = [
  { dayKey: 'd1', planKey: 'easyShort', done: '8.1' },
  { dayKey: 'd2', planKey: 'intervals', done: null },
  { dayKey: 'd3', planKey: 'rest', done: '—' },
  { dayKey: 'd4', planKey: 'tempo', done: null },
  { dayKey: 'd5', planKey: 'easy', done: null },
  { dayKey: 'd6', planKey: 'long', done: null },
  { dayKey: 'd7', planKey: 'rest', done: null },
];

function formatTsb(value) {
  return value > 0 ? `+${value}` : String(value);
}

function Logo({ onClick }) {
  return (
    <button type="button" className="cd-logo" onClick={onClick}>
      <span className="cd-logo-mark" aria-hidden="true">
        <span /><span /><span />
      </span>
      <span className="cd-logo-word">Podium</span>
    </button>
  );
}

function CoachDemoLandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isEn = location.pathname.startsWith('/coaches-demo');

  const [selectedId, setSelectedId] = useState('ana');
  const [decisions, setDecisions] = useState({});
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const wantEn = location.pathname.startsWith('/coaches-demo');
    setLanguage(wantEn ? 'en' : 'pt');
    document.documentElement.lang = wantEn ? 'en' : 'pt';
  }, [location.pathname]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = t('coachDemo.meta.title');

    const robots = document.createElement('meta');
    robots.setAttribute('name', 'robots');
    robots.setAttribute('content', 'noindex, nofollow');
    document.head.appendChild(robots);

    return () => {
      document.title = previousTitle;
      robots.remove();
    };
  }, [t]);

  const selected = useMemo(
    () => ROSTER.find((row) => row.id === selectedId) || ROSTER[0],
    [selectedId]
  );
  const decision = decisions[selectedId] || 'pending';
  const workouts = WORKOUTS[selectedId] || WORKOUTS.ana;

  const handleLanguageSwitch = (lang) => {
    setLanguage(lang);
    const next = lang === 'en' ? '/coaches-demo' : '/treinadores-demo';
    if (next !== location.pathname) navigate(next);
  };

  const handleCta = (event) => {
    event.preventDefault();
    setFormError('');
    if (!email || !email.includes('@')) {
      setFormError(t('coachDemo.cta.error'));
      return;
    }
    setSubmitted(true);
  };

  const statusClass =
    decision === 'approved' ? 'is-ok' : decision === 'edited' ? 'is-edit' : decision === 'rejected' ? 'is-no' : '';

  return (
    <div className="cd-page">
      <div className="cd-banner">
        <div className="cd-wrap cd-banner-inner">
          <span className="cd-banner-mark">{t('coachDemo.banner.label')}</span>
          <p className="cd-banner-text">{t('coachDemo.banner.text')}</p>
          <Link className="cd-banner-link" to={isEn ? '/coaches' : '/treinadores'}>
            {t('coachDemo.banner.compare')}
          </Link>
        </div>
      </div>

      <header className="cd-header">
        <div className="cd-wrap cd-header-inner">
          <Logo onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} />
          <nav className="cd-nav" aria-label={t('coachDemo.nav.label')}>
            <Link className="cd-nav-link" to={isEn ? '/runners' : '/atletas'}>
              {t('coachDemo.nav.athletes')}
            </Link>
            <a className="cd-nav-link" href="https://app.podiumrun.com/login">
              {t('coachDemo.nav.login')}
            </a>
            <button
              type="button"
              className={`cd-lang-btn${isEn ? '' : ' is-on'}`}
              onClick={() => handleLanguageSwitch('pt')}
            >
              PT
            </button>
            <button
              type="button"
              className={`cd-lang-btn${isEn ? ' is-on' : ''}`}
              onClick={() => handleLanguageSwitch('en')}
            >
              EN
            </button>
          </nav>
        </div>
      </header>

      <main>
        <section className="cd-hero">
          <div className="cd-wrap cd-hero-grid">
            <div>
              <p className="cd-kicker">{t('coachDemo.hero.eyebrow')}</p>
              <h1 className="cd-h1">
                {t('coachDemo.hero.title')}{' '}
                <em>{t('coachDemo.hero.titleAccent')}</em>
              </h1>
              <p className="cd-lede">{t('coachDemo.hero.subtitle')}</p>
              <div className="cd-hero-actions">
                <a className="cd-btn cd-btn-primary" href="#cd-cta">
                  {t('coachDemo.hero.cta')}
                </a>
                <span className="cd-cta-note">{t('coachDemo.hero.ctaNote')}</span>
              </div>
              <div className="cd-chips">
                <span className="cd-chip">{t('coachDemo.hero.chip1')}</span>
                <span className="cd-chip">{t('coachDemo.hero.chip2')}</span>
                <span className="cd-chip">{t('coachDemo.hero.chip3')}</span>
              </div>
            </div>

            <div className="cd-stage" aria-label={t('coachDemo.stage.label')}>
              <div className="cd-stage-bar">
                <span>{t('coachDemo.stage.bar')}</span>
                <span className="cd-stage-live">{t('coachDemo.stage.mock')}</span>
              </div>
              <div className="cd-stage-body">
                <div className="cd-roster">
                  <div className="cd-roster-head">
                    <span />
                    <span>{t('coachDemo.roster.athlete')}</span>
                    <span>{t('coachDemo.roster.tsb')}</span>
                    <span>{t('coachDemo.roster.pending')}</span>
                  </div>
                  {ROSTER.map((row) => {
                    const rowDecision = decisions[row.id];
                    const pendingLabel = rowDecision
                      ? t(`coachDemo.decision.${rowDecision}`)
                      : row.pending
                        ? t('coachDemo.roster.sign')
                        : t('coachDemo.roster.clear');
                    return (
                      <button
                        key={row.id}
                        type="button"
                        className={`cd-roster-row${selectedId === row.id ? ' is-on' : ''}`}
                        onClick={() => setSelectedId(row.id)}
                      >
                        <span className={`cd-dot cd-dot-${row.load}`} />
                        <span>
                          <span className="cd-ath-name">{row.name}</span>
                          <span className="cd-ath-meta"> {t(`coachDemo.race.${row.raceKey}`)}</span>
                        </span>
                        <span className="cd-tsb">{formatTsb(row.tsb)}</span>
                        <span className={`cd-pending${row.pending && !rowDecision ? ' is-due' : ''}`}>
                          {pendingLabel}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="cd-card-panel">
                  <article className="cd-approval">
                    <div className="cd-approval-top">
                      <h2 className="cd-approval-name">{selected.name}</h2>
                      <span className="cd-metric">{formatTsb(selected.tsb)}</span>
                    </div>
                    <div className="cd-approval-top">
                      <p className="cd-ath-meta">{t(`coachDemo.race.${selected.raceKey}`)}</p>
                      <span className={`cd-status ${statusClass}`}>
                        {t(`coachDemo.decision.${decision}`)}
                      </span>
                    </div>
                    <div className="cd-workouts">
                      {workouts.map((wo) => (
                        <div key={`${wo.dayKey}-${wo.titleKey}`} className="cd-wo">
                          <span className="cd-wo-day">{t(`coachDemo.week.${wo.dayKey}`)}</span>
                          <span>{t(`coachDemo.wo.${wo.titleKey}`)}</span>
                          <span className="cd-wo-km">{wo.km}</span>
                        </div>
                      ))}
                    </div>
                    <div className="cd-actions">
                      <button
                        type="button"
                        className="cd-btn-sm cd-btn-approve"
                        onClick={() => setDecisions((prev) => ({ ...prev, [selectedId]: 'approved' }))}
                      >
                        {t('coachDemo.approval.approve')}
                      </button>
                      <button
                        type="button"
                        className="cd-btn-sm cd-btn-edit"
                        onClick={() => setDecisions((prev) => ({ ...prev, [selectedId]: 'edited' }))}
                      >
                        {t('coachDemo.approval.edit')}
                      </button>
                      <button
                        type="button"
                        className="cd-btn-sm cd-btn-reject"
                        onClick={() => setDecisions((prev) => ({ ...prev, [selectedId]: 'rejected' }))}
                      >
                        {t('coachDemo.approval.reject')}
                      </button>
                    </div>
                    <div className="cd-gauge">
                      <span className={`cd-dot cd-dot-${selected.load}`} />
                      <div className="cd-gauge-bar" aria-hidden="true">
                        <span /><span /><span /><span />
                      </div>
                      <span className="cd-gauge-read">
                        {t(`coachDemo.load.${selected.load}`)} · TSB {formatTsb(selected.tsb)}
                      </span>
                    </div>
                    <div className="cd-week">
                      {WEEK.map((cell) => (
                        <div key={cell.dayKey} className={`cd-week-cell${cell.done && cell.done !== '—' ? ' is-done' : ''}`}>
                          <div className="cd-week-d">{t(`coachDemo.week.${cell.dayKey}`)}</div>
                          <div className="cd-week-p">{t(`coachDemo.wo.${cell.planKey}`)}</div>
                          <div className="cd-week-r">{cell.done || '·'}</div>
                        </div>
                      ))}
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="cd-section">
          <div className="cd-wrap">
            <p className="cd-kicker">{t('coachDemo.position.kicker')}</p>
            <h2 className="cd-h2">{t('coachDemo.position.title')}</h2>
            <p className="cd-section-lede">{t('coachDemo.position.lede')}</p>
            <div className="cd-trio">
              <article className="cd-tile">
                <p className="cd-tile-who">{t('coachDemo.position.direct.who')}</p>
                <h3>{t('coachDemo.position.direct.title')}</h3>
                <p>{t('coachDemo.position.direct.body')}</p>
              </article>
              <article className="cd-tile">
                <p className="cd-tile-who">{t('coachDemo.position.manual.who')}</p>
                <h3>{t('coachDemo.position.manual.title')}</h3>
                <p>{t('coachDemo.position.manual.body')}</p>
              </article>
              <article className="cd-tile is-podium">
                <p className="cd-tile-who">{t('coachDemo.position.podium.who')}</p>
                <h3>{t('coachDemo.position.podium.title')}</h3>
                <p>{t('coachDemo.position.podium.body')}</p>
              </article>
            </div>
          </div>
        </section>

        <section className="cd-section">
          <div className="cd-wrap">
            <p className="cd-kicker">{t('coachDemo.not.kicker')}</p>
            <h2 className="cd-h2">{t('coachDemo.not.title')}</h2>
            <ul className="cd-not">
              <li><strong>{t('coachDemo.not.item1.label')}</strong> — {t('coachDemo.not.item1.body')}</li>
              <li><strong>{t('coachDemo.not.item2.label')}</strong> — {t('coachDemo.not.item2.body')}</li>
              <li><strong>{t('coachDemo.not.item3.label')}</strong> — {t('coachDemo.not.item3.body')}</li>
            </ul>
          </div>
        </section>

        <section className="cd-section">
          <div className="cd-wrap">
            <p className="cd-kicker">{t('coachDemo.beta.kicker')}</p>
            <h2 className="cd-h2">{t('coachDemo.beta.title')}</h2>
            <p className="cd-section-lede">{t('coachDemo.beta.lede')}</p>
            <div className="cd-trio">
              <article className="cd-tile">
                <h3>{t('coachDemo.beta.item1.title')}</h3>
                <p>{t('coachDemo.beta.item1.body')}</p>
              </article>
              <article className="cd-tile">
                <h3>{t('coachDemo.beta.item2.title')}</h3>
                <p>{t('coachDemo.beta.item2.body')}</p>
              </article>
              <article className="cd-tile">
                <h3>{t('coachDemo.beta.item3.title')}</h3>
                <p>{t('coachDemo.beta.item3.body')}</p>
              </article>
            </div>
          </div>
        </section>

        <section className="cd-section" id="cd-cta">
          <div className="cd-wrap">
            <div className="cd-cta-box">
              <p className="cd-kicker">{t('coachDemo.cta.kicker')}</p>
              <h2 className="cd-h2">{t('coachDemo.cta.title')}</h2>
              <p className="cd-section-lede">{t('coachDemo.cta.body')}</p>
              {submitted ? (
                <div className="cd-success">
                  <h3>{t('coachDemo.cta.successTitle')}</h3>
                  <p>{t('coachDemo.cta.successBody')}</p>
                </div>
              ) : (
                <form className="cd-form" onSubmit={handleCta}>
                  <input
                    className="cd-input"
                    type="email"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      if (formError) setFormError('');
                    }}
                    placeholder={t('coachDemo.cta.placeholder')}
                    aria-label={t('coachDemo.cta.placeholder')}
                  />
                  <button type="submit" className="cd-btn cd-btn-primary">
                    {t('coachDemo.cta.submit')}
                  </button>
                  {formError ? <p className="cd-form-error">{formError}</p> : null}
                </form>
              )}
              <p className="cd-cta-note cd-cta-note-gap">{t('coachDemo.cta.note')}</p>
            </div>
          </div>
        </section>
      </main>

      <footer>
        <div className="cd-wrap cd-footer">
          <span>{t('coachDemo.footer.demo')}</span>
          <span>
            <Link to={isEn ? '/coaches' : '/treinadores'}>{t('coachDemo.footer.live')}</Link>
            {' · '}
            <Link to={isEn ? '/runners' : '/atletas'}>{t('coachDemo.nav.athletes')}</Link>
          </span>
        </div>
      </footer>
    </div>
  );
}

export default CoachDemoLandingPage;
